/**
 * Incident Management Service
 * API client for incident reporting and investigation
 */

import axios from 'axios';
import type {
  Incident,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  IncidentFilters,
  IncidentStatistics,
  IncidentListResponse,
  InvestigationNote,
  IncidentAttachment,
  HashChainVerification,
} from '../types/incident';
import { API_URL } from '../config/apiConfig';

const INCIDENT_SERVICE_URL = `${API_URL}/api/incidents`;

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

// Create axios instance with auth
const createAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`,
  },
});

/**
 * Get list of incidents with optional filters
 */
export const getIncidents = async (
  filters?: IncidentFilters,
  page: number = 1,
  pageSize: number = 20
): Promise<IncidentListResponse> => {
  const params = new URLSearchParams();
  
  if (filters?.hotelId) params.append('hotel_id', filters.hotelId);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.severity) params.append('severity', filters.severity);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.startDate) params.append('start_date', filters.startDate);
  if (filters?.endDate) params.append('end_date', filters.endDate);
  if (filters?.search) params.append('search', filters.search);
  
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const response = await axios.get(
    `${INCIDENT_SERVICE_URL}?${params.toString()}`,
    createAuthHeaders()
  );
  
  return response.data;
};

/**
 * Get incident by ID
 */
export const getIncidentById = async (id: string): Promise<Incident> => {
  const response = await axios.get(
    `${INCIDENT_SERVICE_URL}/${id}`,
    createAuthHeaders()
  );
  
  return response.data;
};

/**
 * Create new incident
 */
export const createIncident = async (
  data: CreateIncidentRequest
): Promise<Incident> => {
  const response = await axios.post(
    INCIDENT_SERVICE_URL,
    data,
    createAuthHeaders()
  );
  
  return response.data;
};

/**
 * Update incident
 */
export const updateIncident = async (
  id: string,
  data: UpdateIncidentRequest
): Promise<Incident> => {
  const response = await axios.put(
    `${INCIDENT_SERVICE_URL}/${id}`,
    data,
    createAuthHeaders()
  );
  
  return response.data;
};

/**
 * Delete incident
 */
export const deleteIncident = async (id: string): Promise<void> => {
  await axios.delete(
    `${INCIDENT_SERVICE_URL}/${id}`,
    createAuthHeaders()
  );
};

/**
 * Add investigation note to incident
 */
export const addInvestigationNote = async (
  incidentId: string,
  content: string
): Promise<InvestigationNote> => {
  const response = await axios.post(
    `${INCIDENT_SERVICE_URL}/${incidentId}/notes`,
    { content },
    createAuthHeaders()
  );
  
  return response.data;
};

/**
 * Upload attachment to incident
 */
export const uploadAttachment = async (
  incidentId: string,
  file: File
): Promise<IncidentAttachment> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${INCIDENT_SERVICE_URL}/${incidentId}/attachments`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  
  return response.data;
};

/**
 * Download attachment
 */
export const downloadAttachment = async (
  incidentId: string,
  attachmentId: string
): Promise<Blob> => {
  const response = await axios.get(
    `${INCIDENT_SERVICE_URL}/${incidentId}/attachments/${attachmentId}`,
    {
      ...createAuthHeaders(),
      responseType: 'blob',
    }
  );
  
  return response.data;
};

/**
 * Delete attachment
 */
export const deleteAttachment = async (
  incidentId: string,
  attachmentId: string
): Promise<void> => {
  await axios.delete(
    `${INCIDENT_SERVICE_URL}/${incidentId}/attachments/${attachmentId}`,
    createAuthHeaders()
  );
};

/**
 * Get incident statistics
 */
export const getIncidentStatistics = async (
  filters?: IncidentFilters
): Promise<IncidentStatistics> => {
  const params = new URLSearchParams();
  
  if (filters?.hotelId) params.append('hotel_id', filters.hotelId);
  if (filters?.startDate) params.append('start_date', filters.startDate);
  if (filters?.endDate) params.append('end_date', filters.endDate);

  const response = await axios.get(
    `${INCIDENT_SERVICE_URL}/statistics?${params.toString()}`,
    createAuthHeaders()
  );
  
  return response.data;
};

/**
 * Verify incident hash chain
 */
export const verifyHashChain = async (
  incidentId: string
): Promise<HashChainVerification> => {
  const response = await axios.get(
    `${INCIDENT_SERVICE_URL}/${incidentId}/verify`,
    createAuthHeaders()
  );
  
  return response.data;
};

/**
 * Export incidents to PDF
 */
export const exportIncidentsToPDF = async (
  filters?: IncidentFilters
): Promise<Blob> => {
  const params = new URLSearchParams();
  
  if (filters?.hotelId) params.append('hotel_id', filters.hotelId);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.severity) params.append('severity', filters.severity);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.startDate) params.append('start_date', filters.startDate);
  if (filters?.endDate) params.append('end_date', filters.endDate);

  const response = await axios.get(
    `${INCIDENT_SERVICE_URL}/export/pdf?${params.toString()}`,
    {
      ...createAuthHeaders(),
      responseType: 'blob',
    }
  );
  
  return response.data;
};

/**
 * Export incidents to CSV
 */
export const exportIncidentsToCSV = async (
  filters?: IncidentFilters
): Promise<Blob> => {
  const params = new URLSearchParams();
  
  if (filters?.hotelId) params.append('hotel_id', filters.hotelId);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.severity) params.append('severity', filters.severity);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.startDate) params.append('start_date', filters.startDate);
  if (filters?.endDate) params.append('end_date', filters.endDate);

  const response = await axios.get(
    `${INCIDENT_SERVICE_URL}/export/csv?${params.toString()}`,
    {
      ...createAuthHeaders(),
      responseType: 'blob',
    }
  );
  
  return response.data;
};

/**
 * Helper function to download blob as file
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
